/**
 * @file build.ts
 * @description Кирпичи для сборки заготовок секций (phase19.16 Р2; SB27):
 * секция по колонкам и короткие конструкторы элементов с нейтральными
 * текстами-заглушками. id элементов — `el-<колонка>-<номер>` (уникальны в блоке).
 *
 * v3 (SB120 этап 1): доли колонок из 12 (`shares`; по умолчанию равные),
 * порядок на телефоне — перестановка; расстояния — «как у сайта» (нет ключей).
 *
 * v4 (SB120 этап 3а): стиль надписи — `preset`; цвет и шрифт элементов —
 * «Авто» (нет `typo`), как и было у заготовок.
 */

import { defaultElement, defaultSectionLook } from "../defaults.js";
import {
  equalShares,
  type SectionElement,
  type SectionElementKind,
  type SectionElementOf,
  type SectionHeader,
  type SectionImageHeight,
  type SectionLook,
  type SectionPayload,
} from "../schema.js";

/** Omit по каждому варианту объединения (обычный Omit схлопывает union до общих ключей). */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** Элемент без id — id проставит `section()` по месту. */
export type DraftElement = DistributiveOmit<SectionElement, "id"> & { id?: string };

export interface SectionDraft {
  header?: SectionHeader;
  /** Доли из 12; нет — равные. */
  shares?: number[];
  valign?: SectionPayload["columns"]["valign"];
  /** Порядок на телефоне (перестановка 1..N); нет — по порядку. */
  phoneOrder?: number[];
  /** Изменения оформления поверх умолчания (вложенные `space` и `visibility` — целиком). */
  look?: Partial<SectionLook>;
  columns: DraftElement[][];
}

/** Секция из колонок элементов; id элементов — по месту. */
export function section(draft: SectionDraft): SectionPayload {
  const items = draft.columns.map((elements, c) => ({
    elements: elements.map((el, e) => ({ ...el, id: el.id ?? `el-${c + 1}-${e + 1}` }) as SectionElement),
  }));
  return {
    ...(draft.header ? { header: draft.header } : {}),
    columns: {
      shares: draft.shares ?? equalShares(items.length),
      valign: draft.valign ?? "center",
      ...(draft.phoneOrder ? { phoneOrder: draft.phoneOrder } : {}),
      items,
    },
    look: { ...defaultSectionLook(), ...draft.look },
  };
}

/** Элемент вида `kind` с умолчаниями и изменениями поверх. */
export function el<K extends SectionElementKind>(
  kind: K,
  over: Partial<Omit<SectionElementOf<K>, "kind" | "id">> = {},
): DraftElement {
  const { id: _id, ...body } = defaultElement(kind);
  return { ...body, ...over } as DraftElement;
}

export const eyebrow = (text: string): DraftElement => el("eyebrow", { text, preset: "caps" });
export const heading = (level: 1 | 2 | 3, text: string): DraftElement => el("heading", { level, text });
export const text = (value: string): DraftElement => el("text", { text: value });
/**
 * Место под фото: пустое, кабинет сразу предлагает медиатеку (SB97, SB21).
 * fit — «По фото», column — «По колонке» (прежнее «Заполнить колонку»),
 * bleed — вырезанный объект за край.
 */
export const photo = (variant: "fit" | "column" | "bleed" = "fit"): DraftElement => {
  const height: SectionImageHeight = variant === "column" ? { kind: "column" } : { kind: "photo" };
  return el("image", { mode: variant === "bleed" ? "bleed" : "fit", height });
};
export const catalogButton = (
  label: string,
  variant: "primary" | "outline" | "link" = "primary",
  inline = false,
): DraftElement => el("button", { label, variant, inline, target: { type: "system", route: "CATALOG" } });

/** Шапка раздела: заголовок H2, по желанию линия и ссылка в каталог. */
export function header(title: string, opts: { line?: boolean; linkLabel?: string } = {}): SectionHeader {
  return {
    title,
    level: 2,
    line: { width: opts.line ? 1 : 0 },
    ...(opts.linkLabel ? { link: { label: opts.linkLabel, target: { type: "system", route: "CATALOG" } } } : {}),
  };
}
